/**
 * Pure image-resolution helper for hotel cards/carousels. Kept free of any
 * next/react imports so it can be unit-tested without the framework runtime.
 *
 * Picks the best single image URL for a hotel:
 *   1. hotel.images (operator/RateHawk selected) — prefers selected!==false
 *   2. hotel.images_ext (RateHawk extended) as fallback
 * Replaces the RateHawk {size} template with a sensible default.
 */
export function resolveHotelImage(hotel, size = "1024x768") {
  const arr =
    (Array.isArray(hotel?.images) && hotel.images.length > 0 && hotel.images) ||
    (Array.isArray(hotel?.images_ext) && hotel.images_ext.length > 0 && hotel.images_ext) ||
    [];
  const selected = arr.filter((img) => img && img.selected !== false);
  const pick = (selected.length > 0 ? selected : arr)[0];
  const raw = typeof pick === "string" ? pick : pick?.url;
  if (!raw) return "";
  return raw.replace("{size}", size);
}

/** Path after `/t/<size>/` (or `/t/{size}/`) so the same photo matches across sizes. */
export function imagePathKey(url) {
  if (typeof url !== "string") return "";
  return url.replace(/\/t\/(?:\d+x\d+|\{size\})\//, "/t/");
}

const SFEER_PREFERRED = ["pool", "spa", "lobby", "restaurant", "bar", "guest_rooms", "hotel_rooms", "balcony", "view", "beach"];
const SFEER_NEUTRAL = ["unspecified", "outside", "meal"];
const SFEER_LAST = ["exterior", "hotel_front"];
const SFEER_NEVER = ["bathroom", "toilet", "meeting_room", "conference", "parking", "hallway", "corridor"];

function sfeerRank(slug) {
  const s = typeof slug === "string" ? slug.trim().toLowerCase() : "";
  if (SFEER_NEVER.includes(s)) return null;
  if (SFEER_PREFERRED.includes(s)) return 0;
  if (SFEER_LAST.includes(s)) return 2;
  // neutral list and unknown/missing categories
  return 1;
}

/**
 * Best "sfeer" photo for a hotel without a hand-picked one. Couples the
 * selected `images` (in order) to `images_ext` (carries category_slug) via the
 * path after /t/<size>/, then prefers atmosphere categories over bare facades
 * and never picks bathrooms, meeting rooms, parking or corridors. Without any
 * category info it falls back to resolveHotelImage.
 */
export function resolveSfeerHotelImage(hotel, size = "1024x768") {
  const ext = Array.isArray(hotel?.images_ext) ? hotel.images_ext : [];
  const catByPath = new Map();
  for (const e of ext) {
    const url = typeof e === "string" ? null : e?.url;
    const slug = typeof e === "string" ? null : e?.category_slug;
    if (url && slug) catByPath.set(imagePathKey(url), slug);
  }
  if (catByPath.size === 0) return resolveHotelImage(hotel, size);

  const imgs = Array.isArray(hotel?.images) ? hotel.images : [];
  const selected = imgs.filter((img) => img && img.selected !== false);
  const candidates = selected.length > 0 ? selected : imgs.length > 0 ? imgs : ext;
  let best = null;
  let bestRank = Infinity;
  for (const img of candidates) {
    const raw = typeof img === "string" ? img : img?.url;
    if (!raw) continue;
    const rank = sfeerRank(catByPath.get(imagePathKey(raw)));
    if (rank === null) continue;
    if (rank < bestRank) {
      best = raw;
      bestRank = rank;
    }
  }
  if (best) return best.replace("{size}", size).replace(/\/t\/\d+x\d+\//, `/t/${size}/`);
  return resolveHotelImage(hotel, size);
}
